import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { ChevronRight, CreditCard, Loader2, Receipt } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Card, CardContent } from '@/components/atoms/card';
import { formatCurrency } from '@/components/molecules/financialImpact';
import { formatDateOnly } from '@/lib/dates/date-only';
import { hasLivePortalSession } from '@/stores/portalAuth.store';
import { usePortalInvoices, type PortalInvoiceListItem } from '@/hooks/portal/usePortalInvoices';
import { PortalShell } from '@/components/portal/PortalShell';
import { PortalStatusPill, type PortalPillTone } from '@/components/portal/PortalStatusPill';

export const Route = createFileRoute('/(portal)/portal/invoices/')({
  beforeLoad: () => {
    if (!hasLivePortalSession()) {
      throw redirect({ to: '/portal/login' });
    }
  },
  component: PortalInvoicesPage,
});

/** Nothing is payable on an open invoice that still has a balance. */
function awaitingPaymentOutcome(inv: PortalInvoiceListItem): boolean {
  return (
    (inv.status === 'OPEN' || inv.status === 'PARTIALLY_PAID') &&
    inv.amountDue <= 0 &&
    inv.balanceDue > 0
  );
}

/**
 * The row's date line follows its pill: only a row that reads Paid says "paid".
 * One still processing or clearing (even a stored PAID one on bank money that
 * has not settled) has not been paid yet.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function invoiceRowDateLabel(inv: PortalInvoiceListItem): string {
  if (!inv.issueDate) return '';
  const paid = invoiceStatus(inv).label === 'Paid';
  return `${paid ? 'paid' : 'issued'} ${formatDateOnly(inv.issueDate)}`;
}

// eslint-disable-next-line react-refresh/only-export-components
export function invoiceStatus(inv: PortalInvoiceListItem): { label: string; tone: PortalPillTone } {
  // Closed-but-not-paid states must keep their real meaning — a voided or
  // written-off invoice is NOT something the customer paid, even though its
  // balance is zero.
  if (inv.status === 'VOID') return { label: 'Void', tone: 'neutral' };
  if (inv.status === 'WRITTEN_OFF') return { label: 'Written off', tone: 'neutral' };
  // A stored PAID invoice on bank money that has not settled reads Clearing,
  // as the invoice page and the staff screens do.
  if (inv.status === 'PAID' && !(inv.processingAmount > 0))
    return { label: 'Paid', tone: 'success' };
  // Nothing more is due, but the bank has not finished: say so, not "Paid".
  if (inv.amountDue <= 0 && inv.processingAmount > 0) return { label: 'Clearing', tone: 'info' };
  // Nothing due while a card or Stripe payment is being verified: its outcome
  // is unknown, so the invoice is not paid yet.
  if (awaitingPaymentOutcome(inv)) return { label: 'Processing', tone: 'info' };
  if (inv.amountDue <= 0) return { label: 'Paid', tone: 'success' };
  if (inv.dueDate) return { label: `Due ${formatDateOnly(inv.dueDate)}`, tone: 'accent' };
  return { label: 'Due', tone: 'accent' };
}

function PortalInvoicesPage() {
  const invoices = usePortalInvoices();
  // Only genuinely-payable invoices count toward "outstanding" and the pay CTA:
  // a VOID invoice keeps a non-zero balanceDue but is cancelled (its row pill
  // already reads "Void"), so balanceDue > 0 alone would nag payment on it.
  const unpaid = (invoices.data ?? []).filter(
    (i) => i.amountDue > 0 && (i.status === 'OPEN' || i.status === 'PARTIALLY_PAID'),
  );
  const outstanding = unpaid.reduce((sum, i) => sum + i.amountDue, 0);
  // The header CTA pays the MOST OVERDUE invoice (earliest due date, then
  // earliest issue date) — the list itself is newest-first, so `unpaid[0]` would
  // be the newest, not the one the customer most needs to clear.
  const payTarget = unpaid.reduce<(typeof unpaid)[number] | undefined>((oldest, inv) => {
    if (!oldest) return inv;
    const key = (i: (typeof unpaid)[number]) => i.dueDate ?? i.issueDate ?? '9999-12-31';
    return key(inv) < key(oldest) ? inv : oldest;
  }, undefined);

  return (
    <PortalShell>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Invoices</h1>
          <p className="text-sm text-muted-foreground">
            {outstanding > 0
              ? `${formatCurrency(outstanding)} outstanding across ${unpaid.length} ${
                  unpaid.length === 1 ? 'invoice' : 'invoices'
                }.`
              : "You're all paid up."}
          </p>
        </div>
        {payTarget ? (
          <Button asChild>
            {/* Pay-one flow: the detail page collects only THIS invoice's
                balance, so the CTA must promise that invoice's amount — not the
                aggregate outstanding (which is shown in the subtitle above). */}
            <Link to="/portal/invoices/$id" params={{ id: payTarget.id }}>
              <CreditCard className="mr-1.5 size-4" />
              {unpaid.length > 1
                ? `Pay invoice ${payTarget.invoiceNumber}`
                : `Pay ${formatCurrency(payTarget.amountDue)}`}
            </Link>
          </Button>
        ) : null}
      </div>

      {invoices.isPending ? (
        <div className="flex justify-center py-16">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : invoices.isError ? (
        <Card>
          <CardContent className="space-y-3 py-6 text-sm text-muted-foreground">
            <p>We couldn&apos;t load your invoices. Please try again.</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void invoices.refetch()}
              disabled={invoices.isFetching}
            >
              {invoices.isFetching ? <Loader2 className="size-4 animate-spin" /> : null}
              Try again
            </Button>
          </CardContent>
        </Card>
      ) : invoices.data.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            You don&apos;t have any invoices yet.
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <div className="divide-y divide-border">
            {invoices.data.map((inv) => (
              <InvoiceRow key={inv.id} invoice={inv} />
            ))}
          </div>
        </Card>
      )}
    </PortalShell>
  );
}

function InvoiceRow({ invoice }: { invoice: PortalInvoiceListItem }) {
  const status = invoiceStatus(invoice);
  const owes = invoice.amountDue > 0;
  // Owed, in-flight and closed-other (void / written-off) rows read "issued …"
  // so we never imply payment.
  const dateLabel = invoiceRowDateLabel(invoice);
  return (
    <Link
      to="/portal/invoices/$id"
      params={{ id: invoice.id }}
      className="flex items-center gap-3 px-4 py-3.5 transition-colors hover:bg-muted/50"
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        <Receipt className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">Invoice {invoice.invoiceNumber}</p>
        {dateLabel ? <p className="truncate text-xs text-muted-foreground">{dateLabel}</p> : null}
      </div>
      <PortalStatusPill label={status.label} tone={status.tone} />
      <span className="w-20 shrink-0 text-right text-sm font-semibold tabular-nums">
        {formatCurrency(owes ? invoice.amountDue : invoice.total)}
      </span>
      <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
    </Link>
  );
}
